<template>
  <div class="viewer-wrapper">
    <!-- Alert -->
    <camera-picker
      class="owal camera picker visible"
      :show="cameraPickerIsOpen"
      @change="pickerDidSelectCamera"
      @close-clicked="cameraPickerIsOpen = false"
    />
    <div
      class="viewer-container"
    >
      <!-- Header -->
      <h3
        class="owal-segment__container owal-segment_top"
      >
        <span
          class="title owal-button__container"
          @click="cameraPickerIsOpen = true"
        >
          <font-awesome-icon
            class="icon camera-icon"
            icon="video"
          />
          {{ title }}
          <font-awesome-icon
            class="icon chevron-right"
            icon="chevron-right"
          />
        </span>
        <div
          class="owal-picker__wrapper"
        >
          <date-time-picker
            v-if="!this.isLive"
            :timestamp="playerTime"
            @blur="pickerBlur"
            @focus="pickerFocus"
            @time-change="skipToMoment"
          />
          <owal-button-toggle
            v-if="canPerformOperationForCurrentService('view_live')"
            :live="this.isLive"
            :callback="this.toggleLive"
            :segments="segments"
          />
        </div>
      </h3>
      <!-- Message -->
      <transition name="fade">
        <div
          v-if="errorMessage"
          class="owal-video-message__container"
        >
          <font-awesome-icon
            class="leading-icon fa-2x"
            icon="exclamation-circle"
          />
          <div
            class="message-text__container"
          >
            <span
              v-if="headerText"
              class="header-text"
            >
              {{ headerText }}
            </span>
            <span
              class="message-text"
            >
              {{ errorMessage }}
            </span>
          </div>
          <font-awesome-icon
            class="trailing-icon close"
            icon="window-close"
            @click="errorMessage = undefined"
          />
        </div>
      </transition>

      <!-- Video Player -->
      <div
        class="video-container attached"
      >
        <owal-video-player
          ref="player"
          :class="playerIsVisible ? 'visible video-player' : 'hidden video-player'"
          :video-source="currentStreamURL"
          :seek-time="seekTime"
          :next-clip-u-r-l="nextClipURL"
          :playlist="playlist"
          @time-update="updatePlayerTime"
          @ended="videoEnded"
        />
        <div
          v-if="!playerIsVisible"
          class="ui segment video-placeholder"
        >
          <h1>
            Loading...
          </h1>
        </div>
      </div>

      <!-- Controls -->
      <controls
        @button-click="handleClick"
        @fullscreen-click="handleFullscreen"
        @forward="skipAhead"
        @back="skipBack"
        @download="requestDownload=true"
        @form-letter="startFormLetter"
        @add-image="addImage"
        @copy-url="copyVideoURL"
      />
      <!-- Long Clip pop up -->
      <owal-long-clip-request
        :show="requestDownload"
        :camera-id="currentCameraUUID"
        @close="requestDownload=false"
        />
      <!-- Detection form panel -->
      <detection-form-panel
        :show="showFormPanel"
        @close="cancelFormLetter"
        @request-snapshot="showFormPanel = false"
        :incidentLocation="formLocation"
        :incidentTime="formTimestamp"
        />
      <!-- Timeline -->
      <timeline
        class="owal-segment_bottom"
        :camera-id="currentCameraUUID"
        :zoom-to="zoomTo"
      />
    </div>
  </div>
</template>

<script>

import { lazyLoad } from '@/lazy'
import { ClipType, getClipURL, getNextClips } from '@/libraries/api/clip'
import moment from 'moment'
import dateTimePicker from '@/components/viewer/DateTimePicker'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { OwalButtonToggle } from '@/libraries/owal-ui'

const Controls = lazyLoad('viewer/control-panel/OwalControlPanel')
const CameraPicker = lazyLoad('shared/CameraPicker')
const OwalLongClipRequest = lazyLoad('viewer/control-panel/OwalLongClipRequest')
const DetectionFormPanel = lazyLoad('viewer/control-panel/DetectionFormPanel')
const OwalVideoPlayer = lazyLoad('viewer/OwalVideoPlayer')

export default {
  name: 'Viewer',
  components: {
    Timeline: lazyLoad('viewer/Timeline'),
    OwalVideoPlayer,
    Controls,
    CameraPicker,
    dateTimePicker,
    OwalButtonToggle,
    OwalLongClipRequest,
    DetectionFormPanel,
  },
  props: {
    timestamp: {
      type: String,
    },
  },
  data () {
    return {
      errorMessage: '',
      headerText: '',
      currentStreamURL: '',
      loading: false,
      clipTimestamp: false,
      playerSeconds: 0,
      timeFormat: 'HH:mm:ss',
      dateFormat: 'MM-DD-YYYY',
      timestampFormat: 'YYYY-MM-DDTHH:mm:ss',
      cameraPickerIsOpen: false,
      seekTime: 0,
      zoomTo: '',
      playlist: [],
      requestDownload: false,
      showFormPanel: false,
      formLocation: undefined,
      formTimestamp: undefined,
    }
  },
  watch: {
    currentCameraUUID: function () {
      this.switchPlaybackToNewCamera()
    },
    timestamp: function (newTimestamp) {
      this.setTimestamp(newTimestamp)
      this.playVideo()
    },
  },
  computed: {
    ...mapState({
      requestedTimestamp: state => state.viewer.archive.timestamp,
    }),
    ...mapGetters({
      isLive: 'viewer/isLive',
      currentDomainUUID: 'currentDomainUUID',
      currentCameraUUID: 'currentCameraUUID',
      domainForCameraUUID: 'domainForCameraUUID',
      canPerformOperationForCurrentService:
        'entitlements/canPerformOperationForCurrentService',
      currentCamera: 'currentCamera',
    }),
    playerIsVisible () {
      return !!this.currentStreamURL && !this.loading
    },
    title () {
      let cameraName = `Unknown Camera (${this.currentCameraUUID})`
      const camera = this.$store.getters.cameraForUUID(this.currentCameraUUID)

      if (camera && camera.description) {
        cameraName = camera.description
      }

      return cameraName
    },
    playerTime () {
      const timestamp = moment(
        this.clipTimestamp ? this.clipTimestamp : this.requestedTimestamp
      ).add({ seconds: this.playerSeconds })
      return timestamp.format()
    },
    pickerDate () {
      const timestamp = moment(this.playerTime)
      return timestamp.format(this.dateFormat)
    },
    pickerTime () {
      const timestamp = moment(this.playerTime)
      return timestamp.format(this.timeFormat)
    },
    nextClipURL () {
      if (this.playlist.length > 0) {
        return this.playlist[0].src
      }
      return undefined
    },
    segments () {
      return [{
        title: 'Live',
        icon: 'podcast',
        iconColor: '#475669',
      },
      {
        title: 'Archive',
        icon: 'archive',
        iconColor: '#475669',
      }]
    },
  },
  methods: {
    ...mapActions({
      startLiveVideoForCameraUUID: 'viewer/startLiveVideoForCameraUUID',
      stopLiveVideo: 'viewer/stopLivestream',
      captureScreenshot: 'viewerTools/captureScreenshot',
    }),
    ...mapMutations({
      setTimestamp: 'viewer/setTimestamp',
      setIsPlaying: 'viewer/setIsPlaying',
      makeDomainCurrentByCameraUUID: 'makeDomainCurrentByCameraUUID',
      setSnapshotMetadata: 'viewerTools/setSnapshotMetadata',
      setIsInvestigating: 'viewerTools/setIsInvestigating',
    }),
    /**
     * Error checks the selected domain and returns the first camera.
     */
    selectDefaultCamera () {
      this.switchPlaybackToNewCamera()
    },
    playVideo () {
      if (this.isLive) {
        this.stopLiveVideo()
      }
      const timestamp = this.requestedTimestamp
      const currentCameraUUID = this.currentCameraUUID
      const clipType = ClipType.SHORT_BEST_AVAILABLE
      this.playlist = [] // At this stage we're calling clips for camera uuid for the first time and can reset the playlist

      this.errorMessage = undefined
      this.headerText = undefined
      this.loading = true
      getClipURL(timestamp, currentCameraUUID, clipType)
        .then(response => {
          const timestampString = response.data.start
            .replace(' ', 'T')
            .replace(/\.\d+/, 'Z')
          this.seekTime = Math.round(response.data.offset_seconds)
          this.clipTimestamp = timestampString
          // console.log('playVideo/getClipURL')
          this.setTimestamp(timestampString)
          this.playerSeconds = 0

          this.currentStreamURL = response.data.pre_signed_url
          this.getPlaylistClips()
        })
        .catch(error => {
          this.errorMessage = error.message
          this.headerText = 'Error'
          const isRecent = moment().diff(moment(timestamp), 'seconds') < 180
          if (isRecent) {
            const refreshIntervalInSeconds = 5
            this.errorMessage = `We are still uploading your video. We'll try to fetch it again in a few seconds.`
            window.setTimeout(this.playVideo, refreshIntervalInSeconds * 1000)
          }
          this.$ga.event({
            eventCategory: `${this.name}-error`,
            eventAction: 'error',
            eventLabel: JSON.stringify({
              message: error.message,
              cameraId: this.currentCameraUUID,
              domainId: this.currentDomainUUID,
              timestamp: this.timestamp,
              isRecent,
            }),
          })
          this.currentStreamURL = undefined
        })
        .then(() => {
          this.loading = false
        })
    },
    player: function () {
      return this.$refs.player
    },
    copyVideoURL () {
      const utc = moment(this.playerTime).utc().format()
      console.log(utc)
      const urlPrefix = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`
      navigator.clipboard.writeText(`${urlPrefix}/view/${this.currentCameraUUID}/${utc}`)
    },
    getPlaylistClips: function () {
      getNextClips(this.currentCameraUUID, this.requestedTimestamp)
        .then(response => {
          if (response.data.ok) {
            this.playlist = []
            response.data.clips.map(clip => {
              const timestamp = clip.start
                .replace(' ', 'T')
                .replace(/\.\d+/, 'Z')
              this.playlist.push({
                src: clip.pre_signed_url,
                type: 'video/mp4',
                timestamp: timestamp,
              })
            })
            this.currentIndex = this.playlist.findIndex(
              clip => clip.timestamp === this.requestedTimestamp
            )
          }
        })
        .catch(error => {
          this.errorMessage = error.message
          this.$ga.event({
            eventCategory: `${this.name}-error`,
            eventAction: 'error',
            eventLabel: JSON.stringify({
              message: error.message,
              cameraId: this.currentCameraUUID,
              domainId: this.currentDomainUUID,
              timestamp: this.timestamp,
            }),
          })

          this.nextClipURL = undefined
        })
        .then(() => {
          this.loading = false
        })
    },
    skipToMoment: function (moment) {
      const newTimestamp = moment.utc().format()
      this.zoomTo = newTimestamp
      const path = {
        path: `/view/${this.currentCameraUUID}/${newTimestamp}`,
      }

      this.$router.push(path)
    },
    switchPlaybackToNewCamera () {
      if (this.isLive) {
        this.stopLiveVideo().then(() => {
          this.startLiveVideo()
        })
      } else {
        this.playVideo()
      }
    },
    // Listener functions //
    /**
     * listener for camera picker, receives target camera UUID and pushes new URL
     */
    pickerDidSelectCamera (currentCameraUUID) {
      this.cameraPickerIsOpen = false
      const newPath = `/view/${currentCameraUUID}/${this.requestedTimestamp}`
      this.$router.push({ path: newPath })
    },
    /**
     * Helper functions for datetime picker.
     * Toggles playback based on interaction.
     */
    pickerFocus () {
      this.player().pause()
      this.setIsPlaying(false)
    },
    pickerBlur () {
      this.player().play()
      this.setIsPlaying(true)
    },
    /**
     * Updates property tracking current playback time in seconds
     */
    updatePlayerTime: function (time) {
      this.playerSeconds = Math.trunc(time)
    },
    /**
     * Listens to control panel. Going away soon
     */
    handleClick: function (identifier, oldIndex, currentIndex) {
      /*
      Don't change this to a computed property!
      According to the docs: https://vuejs.org/v2/guide/components.html#Child-Component-Refs
      */
      const player = this.$refs.player

      /** Each key is an identifier. The action is a function reference. */
      const actions = {
        play: player.togglePlayback,
        stepForward: this.skipAhead,
        stepBackward: this.skipBack,
        hrClip: player.setLongClip,
        speed: player.nextPlaybackRate,
        HDClip: player.longClip,
        history: this.toggleLive,
        fullscreen: player.fullscreen,
      }
      // console.log(`Button: ${identifier}`)
      /** Choose the correct action to take, then invoke it. */
      const action = actions[identifier]
      action()
    },
    /**
     * Transition to fullscreen. Needs to be here because it needs to be user triggered.
     */
    handleFullscreen () {
      this.$refs.player.fullscreen()
    },
    /**
     * Navigate forward and backward in the playlist.
     * Updates the index, source and timestamp then commits the time for reference elsewhere.
     * Seek time is not updated so to simulate jumping back 30 seconds vs jumping to the previous clip.
     * Updates the playlist when there are only two clips left at either end (less than )
     */
    skipAhead () {
      this.currentIndex += 1
      const currentClip = this.playlist[this.currentIndex]
      this.currentStreamURL = currentClip.src
      this.clipTimestamp = currentClip.timestamp

      // console.log('skipAhead')
      this.setTimestamp(currentClip.timestamp)

      if (this.currentIndex > this.playlist.length - 2) {
        this.getPlaylistClips()
      }
    },
    skipBack () {
      this.currentIndex -= 1
      const currentClip = this.playlist[this.currentIndex]
      this.currentStreamURL = currentClip.src
      this.clipTimestamp = currentClip.timestamp

      // console.log('skipBack')
      this.setTimestamp(currentClip.timestamp)

      if (this.currentIndex < 2) {
        this.getPlaylistClips()
      }
    },
    /**
     * Fires when the current clip ends.
     */
    videoEnded: function () {
      this.currentIndex += 1

      const currentClip = this.playlist[this.currentIndex]
      this.currentStreamURL = currentClip.src
      this.clipTimestamp = currentClip.timestamp

      // console.log('videoEnded')
      this.setTimestamp(currentClip.timestamp)

      this.offsetSeconds = 0
      this.playerSeconds = 0
      this.seekTime = 0
      if (this.currentIndex > this.playlist.length - 2) {
        this.getPlaylistClips()
      }
    },
    /**
     * Toggle live viewer mode.
     */
    toggleLive () {
      const isLive = this.isLive
      if (isLive) {
        this.stopLiveVideo().then(() => {
          this.playVideo()
        })
      } else {
        this.startLiveVideo()
      }
    },
    /**
     * Plays live video for the active camera.
     * The stop method is built into the store.
     */
    startLiveVideo () {
      this.currentStreamURL = undefined
      this.startLiveVideoForCameraUUID(this.currentCameraUUID).catch(error => {
        this.$ga.event({
          eventCategory: `viewer-error`,
          eventAction: 'error',
          eventLabel: JSON.stringify({
            message: error.message,
            cameraId: this.currentCameraUUID,
            domainId: this.currentDomainUUID,
            timestamp: this.timestamp,
          }),
        })
        this.playVideo()
      })
    },
    matchDomainToCamera () {
      if (!this.currentCameraUUID) {
        console.debug('No current camera UUID.')
        return
      }

      const domain = this.domainForCameraUUID(this.currentCameraUUID)

      if (!domain) {
        console.debug('No domain to match to camera.')
        return
      }

      const domainUUID = domain.uuid

      if (!domainUUID) {
        console.debug('No domain uuid.')
        return
      }

      const alreadyMatches = this.currentDomainUUID === domainUUID
      if (alreadyMatches) {
        console.debug('Already matches.')
        return
      }
      this.$store.commit(
        'makeDomainCurrentByCameraUUID',
        this.currentCameraUUID
      )
    },
    addImage (imageURL) {
      this.setSnapshotMetadata({
        location: this.currentCamera.description,
        timestamp: this.playerTime,
      })
      this.captureScreenshot({
        cameraUUID: this.currentCameraUUID,
        timestamp: this.playerTime,
      })
      this.showFormPanel = true
    },
    startFormLetter () {
      this.formLocation = this.currentCamera.description
      const time = moment.utc(this.playerTime, 'YYYY-MM-DDTHH:mm:ss')
      this.formTimestamp = time.local().format('MMMM Do, h:mm A')
      this.showFormPanel = true
    },
    cancelFormLetter () {
      this.formLocation = undefined
      this.formTimestamp = undefined
      this.showFormPanel = false
    },
  },
  // Lifecycle functions //
  /**
   * Mounted is called when the Vue instance is rendered.
   * This can be a repeat visit (i.e. deep link > dashboard > viewer calls mounted twice)
   *
   * Matches the displayed domain to the appropriate domain from the currentCameraUUID prop
   * Validates and sets the correct timestamp
   * Kicks off clip getting process
   */
  mounted () {
    // console.log('mounted')
    this.matchDomainToCamera()
    if (this.timestamp === 'live') {
      this.startLiveVideo()
    } else {
      const timestamp = moment(this.timestamp).isValid()
        ? this.timestamp
        : moment()
          .subtract(1, 'minute')
          .utc()
          .format()
      this.setTimestamp(timestamp)
      this.zoomTo = timestamp
      this.playVideo()
    }
  },
}
</script>

<style scoped>
/** A container for the top segment. */
.owal-segment__container {
  background: #f3f4f5;
  padding: 1rem;
  border: 1px solid #d4d4d5;
  box-sizing: border-box;
  margin: 0;
}

/** The container for the camera name button. */
.owal-button__container {
  display: flex;
  align-content: center;
  justify-content: center;
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  margin: 0.25rem;
  font-family: 'Avenir-Medium';
  font-size: 0.75rem;
  color: #475669;
  letter-spacing: 0;
  padding: 0 5px;
  cursor: pointer;
  transition: all 300ms ease;
}

.owal-button__container:hover {
  transform: scale(1.05);
  background: #e0e0e0;
}

/** Wraps the two pickers. */
.owal-picker__wrapper {
  display: flex;
  flex-direction: row;
  height: 3rem;
  justify-content: center;
  flex-shrink: 0;
}

/** Top segment */
.owal-segment_top {
  border-top-left-radius: 0.35rem;
  border-top-right-radius: 0.35rem;
  display: flex;
  flex-direction: row;
  align-content: center;
  align-items: center;
  justify-content: space-between;
}

.owal-segment_bottom {
  border-top-left-radius: 0.35rem;
  border-top-right-radius: 0.35rem;
  /* min-width: 100%; */
}

/** The viewer page wrapper. */
.viewer-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
  padding: 0;
  height: 100%;
  width: 100%;
}

/** The viewer including all of its segments. */
.viewer-container {
  max-width: 93.35rem;
  max-height: 100%;
  height: fit-content;
  width: 100%;
  border-radius: 0.35rem;
  border: 1px solid #d4d4d5;
  box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.2);
  transition: height 300ms ease;
}

/** Message banner */
.owal-video-message__container {
  display: flex;
  width: 100%;
  height: fit-content;
  padding: 1.25rem 1rem;
  background: #eee2e2;
  color: #694747;
  align-items: center;
  align-content: center;
  max-height: 6rem;
}

.owal-video-message__container .leading-icon {
  margin-right: 0.75rem;
}

.owal-video-message__container .message-text__container {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.owal-video-message__container .header-text {
  font-weight: bold;
}

.owal-video-message__container .message-text {
  flex-direction: row;
}

.owal-video-message__container .trailing-icon {
  align-self: flex-start;
  justify-self: flex-end;
  cursor: pointer;
  opacity: 0.5;
  transform: scale(1.1);
  transition: opacity 270ms ease, transform 300ms linear;
}

.owal-video-message__container .trailing-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}
/** The area of the viewer that shows the video and loading indicator. */
.video-container {
  display: block;

  height: 50vmin;
  background: #000;
  margin: 0;
  border-radius: 10px;
}

.hidden.video-player {
  display: none;
}

.title {
  font-family: 'Avenir-Black';
  font-size: 1.75rem;
  color: #475669;
  letter-spacing: 0;
  margin: 0 0.5rem;
}

h3 {
  display: flex;
  justify-content: space-between;
  line-height: 1.9em;
}

input {
  background: none;
  border: none;
  font-family: 'Avenir-Medium';
  color: #475669;
  letter-spacing: 0;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  height: 0;
  max-height: 0;
  padding: 0;
}

.fade-leave,
.fade-enter-to {
  opacity: 1;
  height: fit-content;
  max-height: 22rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms;
}

/** The icons */
.icon {
  height: 3.1428571429rem;
  width: 3.1428571429rem;
  color: #475669;
  transition: all 300ms ease;
  color: inherit;
  padding: 0;
}

.camera-icon {
  transform: scale(0.5);
  margin: 0 -0.05rem;
  margin-left: -0.15rem;
}

.chevron-right {
  transform: scale(0.4);
  margin: 0;
  margin-right: -0.85rem;
  opacity: 0.35;
}

/** the timeline component adds a pixel inset by default, which breaks the illusion. */
.timeline {
  border-radius: 0.45rem;
}

/* Phone */
@media only screen and (max-width: 834px) {
  .viewer-container {
    padding: 0 !important;
  }

  .owal.camera.picker.visible {
    display: block;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1000;
  }

  .owal-picker__wrapper {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    padding: 0;
  }

  .owal-picker__wrapper >>> .viewer-mode-switcher__container {
    justify-content: flex-end;
  }
}

@media only screen and (min-width: 768px) {
  .owal-button__container {
    vertical-align: center;
    justify-content: center;
    align-items: center;
  }

  .owal.camera.picker.visible {
    display: block;
    position: fixed;
    z-index: 1000;
  }
}

@media only screen and (max-width: 414px) {
  .title {
    font-size: 1.45rem !important;
    padding-left: 0 !important;
  }

  h3 {
    flex-direction: column !important;
  }

  .owal-button__container {
    vertical-align: center;
    justify-content: center;
    align-items: center;
    text-align: left;
  }
}
/** Medium size phones */
@media only screen and (min-width: 415px) and (max-width: 834px) {
  /** Adjust button. */
  .owal-button__container {
    font-size: 1.2rem;
    margin-bottom: 0.65rem;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    align-content: center;
  }

  .owal-button__container:hover {
    transform: initial;
  }

  h3 {
    flex-direction: column !important;
  }

  input {
    font-size: 12px !important;
  }
}
</style>
